/**
 * One session on the client's calendar: a button that can be dragged, lifted with the keyboard, or
 * opened.
 *
 * ── Every drag here has a keyboard path AND a second path that is not a reduced one ──────────
 * View ADR §12, and the mobile builder's own failure: its long-press reorder is unreachable to a
 * screen reader. Space lifts, the arrows aim, Space drops, Escape cancels — and clicking opens a
 * popover that writes the SAME `OccurrenceOverride` through a date field, with a time field when
 * the assignment books hours. The popover is not a fallback; it is the only way to retime one
 * session, which a drag cannot say.
 *
 * The lift itself is `shared/ui/dnd/useKeyboardMove`, on its third consumer beside the builder's
 * `MoveableBlock` and `SessionCard`. A calendar's days are ONE run of positions that happens to be
 * drawn seven to a row, which is exactly what that hook's `crossStride` means — so the up and down
 * arrows move by a week without a second axis, and the clamping, the blur exit, the unmount exit
 * and the Escape that does not also close the dialog are all the shared ones.
 *
 * ── The chip is INSIDE the grid's one tab stop, not beside it ───────────────────────────────
 * `tabIndex={-1}`: the cell holds the stop, Enter or Space on it steps in here, and Escape steps
 * back out. A stop per chip made a five-week plan 36 presses deep and put the trainer on the
 * sessions rather than on the days a session can be dropped on. Escape is absorbed on the way out
 * so it moves the stop rather than closing the dialog the trainer is standing in — the same reason
 * `useKeyboardMove` absorbs it while a lift is up.
 *
 * ── The chip carries its own state in its NAME ───────────────────────────────────────────────
 * A dashed border is not announced. `projectionGrid` composes the whole sentence — the title, the
 * date, the hour, and where it was moved from or why it will not happen — so the button's
 * accessible name says what the drawing says.
 */

import { useRef } from 'react';

import { useDraggableItem } from '~/shared/ui/dnd/useDraggableItem';
import { useKeyboardMove } from '~/shared/ui/dnd/useKeyboardMove';

import { CHIP_DRAG_KIND } from '../lib/useProjectionMove';
import type { ChipState, ProjectionChip } from '../model/projectionGrid';

import styles from './AssignPlanDialog.module.css';

interface OccurrenceChipProps {
  readonly chip: ProjectionChip;
  readonly lifted: boolean;
  /** How many days the grid spans — the positions a lift can walk. */
  readonly dayCount: number;
  /** Where this chip sits among them. */
  readonly dayIndex: number;
  /** Space, when nothing is lifted. */
  readonly onLift: () => void;
  /** An arrow while lifted, as the day now aimed at. */
  readonly onAim: (dayIndex: number) => void;
  /** Space again, on the day currently aimed at. */
  readonly onDrop: (dayIndex: number) => void;
  /** Escape, or a blur while lifted. */
  readonly onCancel: () => void;
  /** A click, when nothing is lifted. Receives the chip's element, to anchor to. */
  readonly onOpen: (anchor: HTMLElement) => void;
}

/** How far the vertical arrows move a lifted chip: one row of the calendar. */
const WEEK = 7;

/**
 * What a chip's state adds to its drawing. `planned` adds nothing — it is the resting chip, and a
 * class that declares nothing is a class the stylesheet does not have.
 */
const STATE_CLASS: Readonly<Record<ChipState, string | undefined>> = {
  planned: undefined,
  moved: styles.chipMoved,
  withheld: styles.chipWithheld,
};

export function OccurrenceChip({
  chip,
  lifted,
  dayCount,
  dayIndex,
  onLift,
  onAim,
  onDrop,
  onCancel,
  onOpen,
}: OccurrenceChipProps) {
  const element = useRef<HTMLButtonElement>(null);

  const { isDragging } = useDraggableItem({
    ref: element,
    data: { kind: CHIP_DRAG_KIND, chipKey: chip.key },
    canDrag: true,
  });

  const move = useKeyboardMove({
    count: dayCount,
    index: dayIndex,
    axis: 'horizontal',
    crossStride: WEEK,
    canLift: true,
    onLift,
    onPreview: onAim,
    onCommit: onDrop,
    onCancel,
  });

  return (
    <button
      ref={element}
      type="button"
      // The cell finds its first chip by this, and only by this: a class name is a styling hook
      // and would silently stop matching the day a chip is skinned differently.
      data-chip=""
      tabIndex={-1}
      className={[
        styles.chip,
        STATE_CLASS[chip.state] ?? '',
        lifted ? styles.chipLifted : '',
        isDragging ? styles.chipDragging : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={chip.label}
      data-lifted={lifted ? 'true' : undefined}
      onKeyDown={(event) => {
        // Nothing is lifted and the trainer wants out of this day: the stop goes back to the cell,
        // which is where the arrows mean "another day" again.
        if (event.key === 'Escape' && !lifted && event.target === event.currentTarget) {
          const cell = element.current?.closest<HTMLElement>('[role="gridcell"]');
          if (cell != null) {
            event.preventDefault();
            event.stopPropagation();
            cell.focus();
            return;
          }
        }
        move.host.onKeyDown(event);
      }}
      onBlur={move.host.onBlur}
      onClick={() => {
        const anchor = element.current;
        if (!lifted && anchor !== null) onOpen(anchor);
      }}
    >
      <span className={styles.chipTitle}>{chip.title}</span>
      {chip.time !== null && <span className={styles.chipTime}>{chip.time}</span>}
      {chip.movedFrom !== null && <span className={styles.chipMovedFrom}>{chip.movedFrom}</span>}
    </button>
  );
}
